/**
 * SettingsPage Component
 *
 * Tenant settings page for managing organization configuration.
 * Admin-only access.
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { CopyableCode } from '../components/ui/CopyableCode';
import { LogoUpload } from '../components/settings/LogoUpload';
import { GitHubIntegration } from '../components/settings/GitHubIntegration';
import { useAuth } from '../contexts/AuthContext';
import { useLayoutProps } from '../hooks/useLayoutProps';
import { formatDate } from '../lib/utils/format';
import { hasPermission } from '../lib/utils/permissions';
import type { Tenant } from '../types/tenant.types';

export const SettingsPage: React.FC = () => {
  const { user, tenant, updateTenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const navigate = useNavigate();

  // Handle logo update callback
  const handleLogoUpdated = useCallback(
    (updatedTenant: Tenant) => {
      updateTenant(updatedTenant);
    },
    [updateTenant]
  );

  if (!user || !tenant) {
    return null;
  }

  const canEditSettings = hasPermission(activeRole?.permissions, 'tenant:UpdateSettings');
  const canViewSSO = hasPermission(activeRole?.permissions, 'sso:DescribeProvider');

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Settings</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Manage your organization settings and configuration
          </p>
        </div>

        {/* Organization Information */}
        <Card>
          <Card.Header>
            <Card.Title>Organization Information</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Organization Name
                  </label>
                  <Input
                    type="text"
                    value={tenant.tenantName}
                    disabled
                    placeholder="Organization name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Organization Code
                  </label>
                  <Input
                    type="text"
                    value={tenant.tenantCode}
                    disabled
                    placeholder="org-code"
                  />
                  <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    Used for login (cannot be changed)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Contact Email
                  </label>
                  <Input
                    type="email"
                    value={tenant.adminEmail}
                    disabled
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Status
                  </label>
                  <div className="py-2">
                    <Badge variant={tenant.status === 'active' ? 'success' : 'default'}>
                      {tenant.status.charAt(0).toUpperCase() + tenant.status.slice(1)}
                    </Badge>
                  </div>
                </div>
              </div>

              {tenant.country && (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Country
                    </label>
                    <Input
                      type="text"
                      value={tenant.country}
                      disabled
                      placeholder="Country"
                    />
                  </div>

                  {tenant.city && (
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                        City
                      </label>
                      <Input
                        type="text"
                        value={tenant.city}
                        disabled
                        placeholder="City"
                      />
                    </div>
                  )}
                </div>
              )}

              {canEditSettings && (
                <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <Button variant="secondary" disabled>
                    Edit Organization
                  </Button>
                  <p className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Organization editing features coming soon
                  </p>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Branding (Admin Only) */}
        {canEditSettings && (
          <Card>
            <Card.Header>
              <Card.Title>Branding</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                    Company Logo
                  </label>
                  <LogoUpload
                    tenant={tenant as Tenant}
                    onLogoUpdated={handleLogoUpdated}
                  />
                </div>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  Your logo will be displayed in the application header and on custom login pages.
                </p>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* SSO Configuration */}
        {canViewSSO && (
          <Card>
            <Card.Header>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Card.Title>Single Sign-On (SSO)</Card.Title>
                </div>
                <Badge variant="info">Premium</Badge>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Configure SSO with Microsoft Entra ID or Google to allow users in your organization
                  to sign in with their existing corporate credentials.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => navigate('/app/settings/sso')}
                >
                  Manage SSO Configuration
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* GitHub Integration */}
        {canEditSettings && (
          <GitHubIntegration
            tenant={tenant as Tenant}
            canEdit={canEditSettings}
          />
        )}

        {/* Account Information */}
        <Card>
          <Card.Header>
            <Card.Title>Account Information</Card.Title>
          </Card.Header>
          <Card.Body>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Tenant ID</dt>
                <dd className="mt-1">
                  <CopyableCode value={tenant.tenantId} />
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Created</dt>
                <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  {formatDate(tenant.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Last Updated</dt>
                <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  {formatDate(tenant.updatedAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Verified</dt>
                <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  {tenant.verifiedAt ? (
                    <Badge variant="success">Verified</Badge>
                  ) : (
                    <Badge variant="warning">Pending Verification</Badge>
                  )}
                </dd>
              </div>
            </dl>
          </Card.Body>
        </Card>

        {/* Danger Zone (Admin Only) */}
        {canEditSettings && (
          <Card>
            <Card.Header>
              <Card.Title className="text-red-600">Danger Zone</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  These actions are permanent and cannot be undone. Please proceed with caution.
                </p>
                <div>
                  <Button variant="danger" disabled>
                    Delete Organization
                  </Button>
                  <p className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Contact support to delete your organization
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
